import React, { useState } from 'react';
import {
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Popover,
  useTheme
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import TranslatePopup, { languageOptions } from '../TranslatePopup';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import { setOpenPopupTranslate } from 'src/features/translateArticle';
import { ArrowDropDown } from '@mui/icons-material';

interface IProps {
  handleTranslate: (language: string) => void;
  loading?: boolean;
  isTranslated?: boolean;
  mode?: 'popup' | 'dropdown';
  additionalText?: string;
  handleSeeOriginal?: () => void;
  disabled?: boolean;
}

const TranslateButton: React.FC<IProps> = ({
  handleTranslate,
  loading = false,
  isTranslated = false,
  mode = 'popup',
  additionalText,
  handleSeeOriginal,
  disabled = false
}) => {
  const theme = useTheme();
  const { openPopupTranslate } = useAppSelector(
    (state) => state.storeTranslateArticle
  );
  const dispatch = useAppDispatch();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  return (
    <>
      <Button
        variant="outlined"
        sx={{
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          fontSize: { xs: 11, sm: 14 }
        }}
        onClick={
          mode === 'popup'
            ? () => dispatch(setOpenPopupTranslate(true))
            : isTranslated
            ? () => handleSeeOriginal()
            : (e) => handleClick(e)
        }
        startIcon={<TranslateIcon />}
        disabled={loading || disabled}
      >
        {loading ? (
          <CircularProgress size={24} />
        ) : mode === 'popup' ? (
          'Translate'
        ) : additionalText ? (
          additionalText
        ) : (
          <ArrowDropDown fontSize="small" />
        )}
      </Button>
      {openPopupTranslate && (
        <TranslatePopup
          open={openPopupTranslate}
          onClose={() => dispatch(setOpenPopupTranslate(false))}
          handleTranslate={handleTranslate}
          isTranslated={isTranslated}
          loading={loading}
        />
      )}
      {open && (
        <Popover
          id={id}
          open={open}
          anchorEl={anchorEl}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left'
          }}
        >
          <List>
            {languageOptions.map((lang) => (
              <ListItem
                disablePadding
                key={lang.language}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1
                }}
              >
                <ListItemButton
                  onClick={() => {
                    handleTranslate(lang.language);
                    handleClose();
                  }}
                  sx={{
                    ':hover': {
                      backgroundColor: theme.palette.primary.light
                    }
                  }}
                >
                  <ListItemText primary={lang.name} />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </Popover>
      )}
    </>
  );
};

export default TranslateButton;
